import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Clock, Dumbbell, PlayCircle } from 'lucide-react';
import { coursesApi } from '@/api/endpoints';
import type { CourseDetailResponse } from '@/api/types';
import { CourseCurriculum } from '@/components/courses/CourseCurriculum';
import { formatTotalDuration } from '@/components/courses/duration';
import { PurchaseBox } from '@/components/courses/PurchaseBox';
import { useCourseAccess } from '@/components/courses/useCourseAccess';
import { useCourseProgress } from '@/components/courses/useCourseProgress';
import { MarkdownLite } from '@/components/ui/MarkdownLite';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { useSeo } from '@/hooks/useSeo';
import { courseSeoDescription, courseSeoTitle } from '@/lib/seoText';
import { COURSES_INDEX_PATH, courseStructuredData, coursePath, lessonPath } from '@/lib/structuredData';
import { useInitialData } from '@/ssr/initialData';
import { NotFound } from './NotFound';

/** Kurs sahifasi: /courses/:slug — tavsif, dastur, progress */
export function Course() {
  const { t } = useTranslation();
  const { slug = '' } = useParams();
  const initial = useInitialData();
  const initialCourse = initial?.course?.slug === slug ? initial.course : null;

  const [course, setCourse] = useState<CourseDetailResponse | null>(initialCourse);
  const [notFound, setNotFound] = useState(false);
  const { completed, loggedIn } = useCourseProgress(course?.slug);
  const { hasCourse } = useCourseAccess();

  useEffect(() => {
    if (course?.slug === slug) return;
    let cancelled = false;
    setNotFound(false);
    coursesApi
      .detail(slug)
      .then((res) => {
        if (!cancelled) setCourse(res.data);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });
    return () => {
      cancelled = true;
    };
    // course ataylab kiritilmagan — faqat slug o'zgarganda yuklanadi
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useSeo({
    title: course ? courseSeoTitle(course.title) : 'Video kurs — SQLMaster.uz',
    description: course ? courseSeoDescription(course) : undefined,
    path: coursePath(slug),
    jsonLd: course ? courseStructuredData(course) : undefined,
  });

  if (notFound) return <NotFound />;
  if (!course || course.slug !== slug) {
    return <div className="py-24 text-center text-content-secondary">{t('common.loading')}</div>;
  }

  const owned = hasCourse(course.slug);
  const lessons = course.modules.flatMap((m) => m.lessons);
  const doneCount = lessons.filter((l) => completed.has(l.slug)).length;
  const nextLesson = lessons.find((l) => !completed.has(l.slug)) ?? lessons[0];

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <nav aria-label={t('problem.breadcrumb')} className="mb-4 text-sm">
        <ol className="flex flex-wrap items-center gap-1.5 text-content-secondary">
          <li>
            <Link to="/" className="transition-colors hover:text-content">
              {t('nav.home')}
            </Link>
          </li>
          <li aria-hidden>
            <ChevronRight className="h-3.5 w-3.5" />
          </li>
          <li>
            <Link to={COURSES_INDEX_PATH} className="transition-colors hover:text-content">
              {t('nav.courses')}
            </Link>
          </li>
          <li aria-hidden>
            <ChevronRight className="h-3.5 w-3.5" />
          </li>
          <li aria-current="page" className="text-content">
            {course.title}
          </li>
        </ol>
      </nav>

      <header className="card mb-8 p-6 sm:p-8">
        <span className="tag mb-3 inline-block">{t(`courses.level.${course.level}`)}</span>
        <h1 className="text-2xl font-bold sm:text-3xl">{course.title}</h1>
        <p className="mt-3 max-w-3xl text-content-secondary">{course.shortDescription}</p>

        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-content-secondary">
          <span className="inline-flex items-center gap-1.5">
            <PlayCircle className="h-4 w-4" />
            {t('courses.lessons', { count: course.lessonCount })}
          </span>
          {course.totalDurationSec > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {formatTotalDuration(course.totalDurationSec, t)}
            </span>
          )}
          {course.exerciseCount > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Dumbbell className="h-4 w-4" />
              {t('courses.exercises', { count: course.exerciseCount })}
            </span>
          )}
          {course.freeLessonCount > 0 && (
            <span className="font-medium text-[var(--easy)]">
              {t('courses.freeLessons', { count: course.freeLessonCount })}
            </span>
          )}
        </div>

        <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
          {nextLesson && (
            <Link
              to={lessonPath(course.slug, nextLesson.slug)}
              className="btn btn-primary inline-flex justify-center gap-2 px-6 py-3 text-sm font-semibold"
            >
              <PlayCircle className="h-4 w-4" />
              {doneCount > 0 ? t('courses.continue') : t('courses.start')}
            </Link>
          )}
          {loggedIn ? (
            <div className="flex-1 sm:max-w-xs">
              <p className="mb-1.5 text-xs text-content-muted">
                {t('courses.progress', { done: doneCount, total: lessons.length })}
              </p>
              <ProgressBar value={doneCount} max={lessons.length} />
            </div>
          ) : (
            <p className="text-xs text-content-muted">{t('courses.loginForProgress')}</p>
          )}
        </div>

        <PurchaseBox
          kind="course"
          title={course.title}
          priceUzs={course.priceUzs}
          owned={owned}
          className="mt-6 border-t border-line pt-6"
        />
      </header>

      {course.descriptionMd && (
        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold">{t('courses.aboutCourse')}</h2>
          <MarkdownLite text={course.descriptionMd} />
        </section>
      )}

      <section>
        <h2 className="mb-4 text-xl font-bold">{t('courses.curriculum')}</h2>
        <CourseCurriculum course={course} completed={completed} hasAccess={owned} />
      </section>
    </div>
  );
}
